/* eslint-disable no-console */
import { CommonModule } from '@angular/common';
import { Component, Inject, signal, inject, effect, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AccessionCardComponent } from '@/app/shared/cards/accession-card/accession-card.component';
import { PaginationComponent } from '@/app/shared/pagination/pagination.component';
import { SearchBarComponent } from '@/app/shared/search-bar/search-bar.component';
import { TabsComponent } from '@/app/shared/tabs/tabs.component';
import { MyAccessionsService } from '@/app/services/my-accessions.service';
import { filtersBasic } from '@/app/interfaces/filtersBasic';
import { filtersAdvanced } from '@/app/interfaces/filtersAdvanced';
import { FiltersComponent } from '@/app/shared/filters/filters.component';
import { DropdownComponent } from '@/app/shared/dropdown/dropdown.component';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AccessionsList, ApprovedActions } from '@/app/interfaces/accessionsList';

@Component({
  selector: 'app-my-accessions-list-received',
  imports: [
    ConfirmDialog,
    AccessionCardComponent,
    TabsComponent,
    CommonModule,
    RouterModule,
    PaginationComponent,
    SearchBarComponent,
    FiltersComponent,
    DropdownComponent,
    ButtonsComponent,
  ],
  templateUrl: './my-accessions-list-received.component.html',
  styleUrl: './my-accessions-list-received.component.scss',
  standalone: true,
  providers: [ConfirmationService],
})
export class MyAccessionsListReceivedComponent implements OnInit {
  constructor(
    @Inject(MyAccessionsService) private myAccessionsService: MyAccessionsService,
    private confirmationService: ConfirmationService
  ) {}
  accessionsEffect = effect(() => {
    this.accessions.set([]);
    this.currentPage();
    this.paginationSize();
    this.loadAccessions();
  });
  organisms = [];
  paginationSize = signal(15);
  currentPage = signal(1);
  accessions = signal<AccessionsList[]>([]);
  seachData = signal('');
  private route = inject(ActivatedRoute);
  totalResults = signal(0);
  tabsItems = signal([
    { title: 'Enviadas', url: '/mis-adhesiones/listado/enviadas', id: 'sent' },
    { title: 'Recibidas', url: '/mis-adhesiones/listado/recibidas', id: 'received' },
  ]);
  orgCode = JSON.parse(localStorage.getItem('userData')).orgCode;
  jobCode = JSON.parse(localStorage.getItem('userData')).jobCode;

  advancedFilters: filtersAdvanced[] = [
    {
      type: 'multiselect',
      label: 'Estados',
      options: [
        { label: 'APROBADAS', value: 'APPROVED' },
        { label: 'RECHAZADAS', value: 'REJECTED' },
        { label: 'PENDIENTES', value: 'PENDING' },
        { label: 'BLOQUEADAS', value: 'BLOCKED' },
        { label: 'FINALIZADAS', value: 'FINISHED' },
      ],
      selected: '',
    },
    {
      type: 'dateRange',
      label: 'Fecha de creación',
      dateRangeOptions: {
        startDate: null,
        endDate: null,
      },
    },
    {
      type: 'dateRange',
      label: 'Fecha de modificación',
      dateRangeOptions: {
        startDate: null,
        endDate: null,
      },
    },
  ];
  orderby: filtersBasic = {
    type: 'dropdown',
    label: 'Ordenar adhesiones',
    classes: 'text-sm py-1',
    icon: 'pi pi-sort-alt',
    options: [
      { label: 'Ordenar por más reciente', value: 'recent' },
      { label: 'Ordenar por menos reciente', value: 'oldest' },
      { label: 'Ordenar por última modificación', value: 'update' },
    ],
    selected: '',
  };
  orderByName = signal('fecha_registro');
  orderDirection = signal('asc');

  get filterParams() {
    const loggedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hasDpdRole = loggedUserData?.roles?.dpd ? true : false;

    return [
      {
        basicFilter: {
          orderBy: this.orderDirection(),
          nameOrderBy: this.orderByName(),
          currentPage: this.currentPage(),
          sizePages: this.paginationSize(),
          orgCode: this.orgCode,
          dpd: hasDpdRole,
        },
      },
      {
        advanceFilter: {
          state: this.advancedFilters.find((e) => e.label === 'Estados')?.selected,
          creationDateFrom: this.advancedFilters.find((e) => e.label === 'Fecha de creación')?.dateRangeOptions
            ?.startDate,
          creationDateTo: this.advancedFilters.find((e) => e.label === 'Fecha de creación')?.dateRangeOptions?.endDate,
          modifiedDateFrom: this.advancedFilters.find((e) => e.label === 'Fecha de modificación')?.dateRangeOptions
            ?.startDate,
          modifiedDateTo: this.advancedFilters.find((e) => e.label === 'Fecha de modificación')?.dateRangeOptions
            ?.endDate,
          search: this.seachData(),
        },
      },
    ];
  }

  confirm(accession, value) {
    this.confirmationService.confirm({
      message: 'Va a modificar el estado de una adhesión. ¿Está seguro/a de que desea continuar?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Continuar',
      },
      accept: () => {
        this.handleActionAccesion(accession, value);
      },
    });
  }

  onDropdownChange($event: string) {
    switch ($event) {
      case 'recent':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('desc');
        }
        break;
      case 'oldest':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('asc');
        }
        break;
      case 'update':
        {
          this.orderByName.set('fecha_ultima_modificación');
          this.orderDirection.set('desc');
        }
        break;
      default:
    }
  }

  handleApplyFilters(e) {
    this.advancedFilters = e;
    this.loadAccessions();
  }
  handleSearch(value: string) {
    this.seachData.set(value);
  }

  async handleActionAccesion(accesion, newValue) {
    if (accesion) {
      const params = {
        idAdhesion: accesion.id,
        state: newValue,
        modifiedDate: new Date(),
        deadline: null,
        userRole: null,
      };

      const loggedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (loggedUserData?.roles?.dpd) {
        params.userRole = 'DPD';
      } else if (loggedUserData?.roles?.jefe_servicio) {
        params.userRole = 'JS';
      }

      // obCode
      // idAdhesion
      // state
      // modifiedDate
      // y si se cambia de estado a finalizar tb hay que mandar fecha_limite
      if (newValue === 'finished') {
        params.deadline = new Date();
      } else {
        delete params.deadline;
      }
      const response = await this.myAccessionsService.updateAccesion(params);
      if (response) {
        this.loadAccessions();
      }
    }
  }

  getValidatedTime(accession: AccessionsList): Date {
    const effectiveDate = accession.effectiveDate;
    return effectiveDate ? new Date(effectiveDate) : this.getCreateDate(accession);
  }

  getCreateDate(accession: AccessionsList): Date {
    return new Date(accession.creationDate);
  }

  getAccessionId(accession: AccessionsList): string {
    return String(accession.id);
  }

  getApprovedActions(accession: AccessionsList): ApprovedActions {
    if (accession.approvedActions?.options?.length) {
      return accession.approvedActions;
    }
    return null;
  }

  private async loadAccessions() {
    const response = (await this.myAccessionsService.getAllAccessionsReceived(this.filterParams)) as {
      data: { items: AccessionsList[]; total: number };
      message: string;
    };
    if (response.message !== 'OK') {
      return;
    } else {
      response.data.items.forEach((e) => {
        e.approvedActions = {
          label: 'Más',
          options: [],
        };
        switch (e.state?.toLowerCase()) {
          case 'approved':
            e.approvedActions.options.push(
              {
                label: 'Finalizar',
                value: 'finished',
                action: () => this.confirm(e, 'finished'),
              },
              {
                label: 'Bloquear',
                value: 'blocked',
                action: () => this.confirm(e, 'blocked'),
              }
            );
            // finalizar y bloquear
            break;
          case 'pending':
            e.approvedActions.options.push(
              {
                label: 'Aprobar',
                value: 'approved',
                action: () => this.confirm(e, 'approved'),
              },
              {
                label: 'Rechazar',
                value: 'rejected',
                action: () => this.confirm(e, 'rejected'),
              }
            );
            // aprobar rechazar
            break;
          case 'blocked':
            e.approvedActions.options.push(
              {
                label: 'Aprobar',
                value: 'approved',
                action: () => this.confirm(e, 'approved'),
              },
              {
                label: 'Finalizar',
                value: 'finished',
                action: () => this.confirm(e, 'finished'),
              }
            );
            break;

          default:
            break;
        }
      });
      this.accessions.set(response.data.items);
      this.totalResults.set(response.data.total);
    }
  }

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params?.['state']) {
        this.advancedFilters.find((e) => e.label === 'Estados').selected = [params['state'].toUpperCase()];
      }
    });
  }
}
